import _ from "lodash";
import React from "react";
import BenchmarkView from "UIComponents/BenchmarkView";
import Accordian from "UIComponents/Accordian";
import { Checkbox, EmptyState } from "@toddle-edu/ds-web";
import classes from "./TagLearningGoalsList.scss";
import styles from "./TagLearningGoalsListStyles";
import {
  getPYPElementsRowsFromField,
  getPYPElementsRowsFromUID,
  isEmpty,
  buildSlimTree,
  getUniqAssociatedParentsOfNodes,
  mergeAssociatedParentsWithNodes,
} from "Utils";
import ACLStore from "lib/aclStore";
import {
  GoalIllustration,
  NoSearchInFilterIllustration,
} from "@toddle-edu/ds-theme";
import SideNavigation from "./SideNavigation";
import { getElementValue } from "modules/ElementValueUtils";
import { pypElementMapping } from "Constants/plannerElementMappings";
import { Popover } from "@toddle-edu/react-tiny-popover";

import SubjectBenchmarks from "./SubjectBenchmarksV2";
import HighlightSearchText from "./HighlightSearchText";
import {
  getIsNewGoalTaggingEnabled,
  getPostEntity,
  isAssessmentTagged,
  getTaggedGoalsCount,
  getIsNewGradingSetupEnabled,
  getStandardEdges,
  getIsSubjectStandardsFilterEnabled,
  getIsStandardsRevampEnabled,
  getIsStandardsV2,
  getShowNewStandardsUIInV1,
} from "Post/utils";
import { getIsSubjectStandard } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/utils";
import StandardTable from "./StandardTable/StandardTable";
import { STANDARD_UIDS } from "modules/Services/ServicesConstants";
import { getPlannerElementValue } from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/components/Assessments/modules/Utils";
import { getFlattenedNestedNodes } from "../Utils";
import { isParentTaggingEnabledForType } from "AppComponents/DatasetTaggingBody/modules/Utils";

const JOURNAL_POST_CREATION_GOAL = "journal-postcreation-goal";
const BLANK_ARRAY = [];
const BLANK_OBJECT = {};

const getLeValues = ({ fields, fieldUid }) => {
  const leValues = [];
  const standardFields = _.filter(
    fields,
    field => _.get(field, "uid") === fieldUid
  );
  _.forEach(standardFields, field => {
    leValues.push(..._.defaultTo(_.get(field, "value"), []));
  });
  return _.uniq(leValues);
};

const emptyStateSubtitleStyleUnit = {
  maxWidth: "300px",
  lineHeight: "24px",
};
const emptyStateSubtitleStyle = {
  lineHeight: "24px",
};

const getBenchMarkNodesMemoized = _.memoize(filteredField => {
  const elementsIdMap = {};
  _.forEach(_.get(filteredField, "rows"), node => {
    _.set(elementsIdMap, _.get(node, "id"), true);
  });
  return elementsIdMap;
});

const getElementValueMemoize = _.memoize(
  getElementValue,
  ({ valueKeys, fieldUID, benchmarkSet }) => {
    // Create optimized cache key with essential data only
    const cacheData = {
      fieldUID,
      valueKeysHash: valueKeys.join("|"),
      // Only include essential benchmark set data that affects the output
      nodesCount: benchmarkSet?.nodes?.length || 0,
      rootNodesHash: benchmarkSet?.rootNodes?.join("|") || "",
    };

    return JSON.stringify(cacheData);
  }
);

const getContainerStyle = (isDepth2, isSubjectStandard) => {
  const baseStyle = isDepth2
    ? styles.itemsViewContainerStyleDepth2
    : styles.itemsViewContainerStyle;

  return isSubjectStandard
    ? { ...baseStyle, ...styles.standardTableContainer }
    : baseStyle;
};

class TagLearningGoalsList extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      updateRef,
      selectElementsRef,
      filtersRef,
      tags,
      updateShowFilters,
      curriculumType,
      learningCourseId,
      selectFromAllPYP,
    } = this.props;
    const { props: screenPanelProp } = this.props;
    this.searchSectionRef = React.createRef(null);
    this.standardViewRef = React.createRef(null);
    this.initialFieldsRef = React.createRef(null);
    this.standardCountRef = React.createRef(0);
    this.standardSetMapFromProps = this.props.standardSetMap;
    if (selectElementsRef) {
      selectElementsRef.current = this.onSelectGoals;
    }
    if (updateRef) {
      updateRef.current = this.onNextClick;
    }
    if (filtersRef) {
      filtersRef.current = tags;
    }
    const elementsPYP = _.get(screenPanelProp, "postDetails.elementPYP", []);
    const selectedPypElement = _.get(
      screenPanelProp,
      "postDetails.selectedPypElement",
      ""
    );
    const selectedElements = _.get(
      screenPanelProp,
      "postDetails.selectedElementsPYP",
      []
    );
    const initialFields = this.getInitialFields(props);
    this.initialFieldsRef.current = initialFields;
    if (_.size(initialFields) === 1) {
      updateShowFilters(
        _.get(initialFields, "[0].uid") === STANDARD_UIDS[curriculumType]
      );
    }
    this.standardCountRef.current = _.sumBy(
      _.values(this.props.standardSetMap),
      arr => _.size(arr)
    );
    const allSelectedPYPElements = this.getAllFilteredSelectedElements({
      initialFields,
    });

    this.standardCountRef.current = _.sumBy(
      _.values(this.props.standardSetMap),
      arr => _.size(arr)
    );

    const pypElementsCount = _.reduce(
      allSelectedPYPElements,
      (count, element) => {
        const isSubjectStandard =
          getIsNewGradingSetupEnabled() &&
          learningCourseId &&
          getIsSubjectStandard({
            type: _.get(pypElementMapping, element.fieldUID),
          });

        return (
          count + (isSubjectStandard ? 0 : _.get(element, "value.length", 0))
        );
      },
      0
    );
    const standardsCount =
      getIsNewGradingSetupEnabled() &&
      learningCourseId &&
      (selectFromAllPYP ? !getIsStandardsRevampEnabled() : true)
        ? _.sum(
            _.values(_.mapValues(this.props.standardSetMap, arr => _.size(arr)))
          )
        : 0;
    this.props.updateTotalCount(pypElementsCount + standardsCount);

    this.state = {
      selectedElementsPYP:
        getIsNewGoalTaggingEnabled() && !_.isEmpty(selectedElements)
          ? _.cloneDeep(selectedElements)
          : _.cloneDeep(elementsPYP) ?? [], //adding this to prevent changes in learning goals count in recent post
      searchTerm: this.props.searchTerm || "",
      selectedFilterValue: {},
      selectedFilterValues: [], // in new flow, multiple goals can be selected from filter.
      showSearchFilter: false,
      initialFields,
      filteredFields: [],
      searchFilterData: [],
      allSelectedPYPElements: allSelectedPYPElements,
      selectedPypElement: getIsSubjectStandardsFilterEnabled()
        ? selectedPypElement
        : "",
      selectedPypElementSecondary: "",
      selectedStandardValues: [],
      selectedStandardFilter: {},
      showStandardFilter: false,
      filteredStandardData: [],
      allSelectedStandards: [],
      selectedStandards:
        _.find(elementsPYP, { fieldUID: STANDARD_UIDS[curriculumType] })
          ?.value || [],
      searchStandardValues: {},
      selectedDataGridRows: [],
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.searchTerm !== prevProps.searchTerm) {
      this.updateFields({ searchTerm: this.props.searchTerm });
    }
    const isSubjectStandardsFilterEnabled = getIsSubjectStandardsFilterEnabled();

    if (
      (this.props.benchmarkData != prevProps.benchmarkData ||
        this.props.plannerElements != prevProps.plannerElements ||
        this.props.subjectGradePlannerElements !=
          prevProps.subjectGradePlannerElements) &&
      isSubjectStandardsFilterEnabled
    ) {
      const initialFields = this.getInitialFields(this.props);
      this.setState({
        initialFields,
      });
      this.updateFields({ initialFields });
      this.props.updateStateChange(true);
    }

    if (
      this.props.parentType === "UNIT_PLAN" &&
      _.size(this.props.fields) !== _.size(prevProps.fields)
    ) {
      const initialFields = this.getInitialFields(this.props);
      this.setState({
        initialFields,
      });
      this.updateFields({ initialFields });

      const pypElementsCount = _.reduce(
        _.get(this.props, "fields", []),
        (count, element) => {
          return count + _.get(element, "value.length", 0);
        },
        0
      );
      this.props.updateTotalCount(pypElementsCount);

      this.props.updateStateChange(true);
    }

    //Removing this for now had added this for accessiblity to select first element on search
    // if (this.searchSectionRef) {
    //   const elementNodes = getElementNodesMemoized(
    //     this.searchSectionRef?.current
    //   );
    //   _.first(elementNodes)?.focus();
    // }

    if (this.props.selectAllChecked !== prevProps.selectAllChecked) {
      this.toggleSelectAll();
    }
    const { isStateChanged, updateStateChange } = this.props;
    if (
      !isStateChanged &&
      !_.isEqual(prevState.selectedElementsPYP, this.state.selectedElementsPYP)
    ) {
      updateStateChange(true);
    }
  }
  componentDidMount() {
    const {
      searchTerm,
      selectedFilterValue,
      selectedFilterValues,
      initialFields,
    } = this.state;
    const { curriculumType, props: screenPanelProp } = this.props;
    const selectedPypElementFromProp = _.get(
      screenPanelProp,
      "postDetails.selectedPypElement",
      ""
    );
    const standardUID = STANDARD_UIDS[curriculumType];
    let hasOnlySubjectStandards = false;
    const standardFields = initialFields.filter(
      field => field.uid === standardUID && !_.isEmpty(field.nodes)
    );
    const otherFieldsValid = initialFields.filter(
      field => field.uid !== standardUID
    );
    const isAllOtherFieldsEmpty = _.every(otherFieldsValid, field =>
      _.isEmpty(field.nodes)
    );

    if (_.size(standardFields) === 1 && isAllOtherFieldsEmpty) {
      hasOnlySubjectStandards = true;
    }
    this.updateFields({
      searchTerm,
      selectedFilterValue,
      selectedFilterValues,
      selectedPypElement:
        hasOnlySubjectStandards && getIsSubjectStandardsFilterEnabled()
          ? STANDARD_UIDS[curriculumType]
          : selectedPypElementFromProp,
    });

    const taggableElements = this.getFilteredTaggablePYPElements();
    const updatedSelectedFilterValues = [];
    _.forEach(taggableElements, element => {
      const isNestedView = _.get(element, "isNestedView", false);
      if (isNestedView == true) {
        _.forEach(_.get(element, "rootNodes", []), node => {
          updatedSelectedFilterValues.push(
            JSON.stringify({
              uid: _.get(element, "value"),
              value: _.get(node, "id"),
              subTopicLabel: _.get(node, "label", ""),
            })
          );
        });
      } else {
        updatedSelectedFilterValues.push(
          JSON.stringify({
            uid: _.get(element, "value"),
            value: _.get(element, "value"),
          })
        );
      }
    });
    this.setState({
      selectedFilterValues: updatedSelectedFilterValues,
      selectedPypElement:
        hasOnlySubjectStandards && getIsSubjectStandardsFilterEnabled()
          ? STANDARD_UIDS[curriculumType]
          : selectedPypElementFromProp,
    });
    // Will update the count in footer when updating post
    this.updateSelectedElementsCount();
  }

  getCurrentParentType = () => {
    const { selectFromAllPYP, parentType } = this.props;
    return selectFromAllPYP
      ? "ALL"
      : parentType == "UNIT_PLAN"
      ? "UNIT_PLAN"
      : "RESOURCE";
  };

  getAllFilteredSelectedElements = ({ initialFields }) => {
    const { taggableElements, selectFromAllPYP } = this.props;
    const selectedElementsPYP = [];
    _.forEach(taggableElements, taggableElement => {
      const fieldUID = taggableElement.value;
      const field = _.find(
        initialFields,
        field => _.get(field, "uid") == fieldUID
      );

      const nodes = _.get(field, "nodes");
      let value = [];
      const elementsIdMap = {};
      _.forEach(nodes, node => {
        _.set(elementsIdMap, _.get(node, "id"), true);
      });
      if (taggableElement.isNestedView) {
        if (
          _.get(taggableElement, "value") === "benchmarks" &&
          !selectFromAllPYP
        ) {
          value = _.map(
            _.filter(nodes, node => {
              const isLeaf =
                _.get(node, "isLeaf") ||
                !_.some(_.get(node, "children"), childId =>
                  _.has(elementsIdMap, childId)
                );

              return isLeaf;
            }),
            item => item.id
          );
        } else {
          value = _.map(
            _.filter(
              nodes,
              node =>
                node.isLeaf ||
                _.isEmpty(_.get(node, "children")) ||
                !_.some(_.get(node, "children"), childId =>
                  _.has(elementsIdMap, childId)
                )
            ),
            item => item.id
          );
        }
      } else {
        value = _.map(nodes, item => item.id);
      }

      if (!_.isEmpty(value)) {
        selectedElementsPYP.push({
          fieldUID: taggableElement.value,
          value,
          parentType: this.getCurrentParentType(),
        });
      }
    });

    return selectedElementsPYP;
  };

  toggleSelectAll = () => {
    const { selectedElementsPYP, allSelectedPYPElements } = this.state;
    const { learningCourseId, selectFromAllPYP } = this.props;
    if (getIsNewGradingSetupEnabled() && learningCourseId) {
      const keys = _.keys(this.props.standardSetMap);
      const allStandards = _.flatMap(keys, key =>
        _.map(this.props.standardSetMap[key], item => item.node.id)
      );
      const allElementsExceptStandards = _.filter(
        allSelectedPYPElements,
        item =>
          !getIsSubjectStandard({
            type: _.get(pypElementMapping, item.fieldUID),
          })
      );
      const excludeSelectedStandards = _.filter(
        selectedElementsPYP,
        item =>
          !getIsSubjectStandard({
            type: _.get(pypElementMapping, item.fieldUID),
          })
      );
      const selectedStandards = _.filter(selectedElementsPYP, item =>
        getIsSubjectStandard({
          type: _.get(pypElementMapping, item.fieldUID),
        })
      );
      const allSelectedPYPElementsClone = _.map(
        getIsStandardsRevampEnabled() && selectFromAllPYP
          ? allElementsExceptStandards
          : allSelectedPYPElements,
        item => {
          if (
            getIsSubjectStandard({
              type: _.get(pypElementMapping, item.fieldUID),
            })
          ) {
            item.value = allStandards;
          }
          return item;
        }
      );

      if (
        !_.isEmpty(
          getIsStandardsRevampEnabled() && selectFromAllPYP
            ? excludeSelectedStandards
            : selectedElementsPYP
        )
      ) {
        if (getIsStandardsRevampEnabled() && selectFromAllPYP) {
          this.setState({
            selectedElementsPYP: selectedStandards,
          });
        } else {
          this.setState({ selectedElementsPYP: [], selectedStandards: [] });
        }
      } else {
        this.setState({
          selectedElementsPYP:
            getIsStandardsRevampEnabled() && selectFromAllPYP
              ? _.uniqBy(
                  [...allSelectedPYPElementsClone, ...selectedStandards],
                  "fieldUID"
                )
              : allSelectedPYPElementsClone,
          selectedStandards: allStandards,
        });
      }
    } else {
      if (!_.isEmpty(selectedElementsPYP)) {
        this.setState({ selectedElementsPYP: [] });
      } else {
        this.setState({
          selectedElementsPYP: allSelectedPYPElements,
        });
      }
    }
    setTimeout(() => {
      this.updateSelectedElementsCount();
    }, 100);
  };

  onNextClick = () => {
    const {
      props: screenPanelProps,
      selectFromPost,
      setToastMsg,
      postTaggedEntity,
      curriculumProgramId,
    } = this.props;
    const { goToNextScreen, updatePost } = screenPanelProps;
    const { selectedElementsPYP } = this.state;

    updatePost({
      elementPYP: this.updatedSelectedElements(),
      selectedElementsPYP: undefined,
      standardsTagFilter: [],
      selectedPypElement: undefined,
    });
    if (!_.isEmpty(postTaggedEntity) && getIsNewGoalTaggingEnabled()) {
      const learningGoalsCount = getTaggedGoalsCount(
        this.updatedSelectedElements()
      );
      if (!isAssessmentTagged(postTaggedEntity)) {
        setToastMsg({
          msg:
            learningGoalsCount > 1
              ? "journal:count_of_units_goals_tagged_plural"
              : "journal:count_of_units_goals_tagged",
          locale_params: [
            {
              key: "count",
              value: _.size(getPostEntity(postTaggedEntity, "UNIT_PLAN")),
              isPlainText: true,
            },
            {
              key: "goalCount",
              value: learningGoalsCount,
              isPlainText: true,
            },
          ],
          type: "success",
        });
      } else if (isAssessmentTagged(postTaggedEntity)) {
        setToastMsg({
          msg:
            learningGoalsCount > 1
              ? "journal:le_goal_tagged_plural"
              : "journal:le_goal_tagged",
          locale_params: [
            {
              key: "count",
              value: learningGoalsCount,
              isPlainText: true,
            },
          ],
          type: "success",
        });
      }
    } else if (_.isEmpty(postTaggedEntity) && getIsNewGoalTaggingEnabled()) {
      updatePost({
        postTaggedEntity: [
          {
            entityId: curriculumProgramId,
            entityType: "CURRICULUM_PROGRAM",
          },
        ],
      });
    }
    selectFromPost && updatePost({ selectFromPost: false });
    goToNextScreen();
  };

  onSelectGoals = () => {
    const {
      props: { updatePost },
    } = this.props;
    const { selectedElementsPYP } = this.state;
    updatePost({
      selectedElementsPYP: selectedElementsPYP,
    });
  };

  getResolvedMinimalTree = ({
    fieldUID,
    curriculumType,
    learningCourseId,
    selectFromAllPYP,
    selectedElementsPYP,
    field,
    nodeKey,
  }) => {
    const isSubjectStandard =
      getIsSubjectStandard({
        type: _.get(pypElementMapping, fieldUID),
      }) &&
      getIsNewGradingSetupEnabled() &&
      learningCourseId;

    if (isSubjectStandard) {
      return {
        [nodeKey]: _.map(
          _.flatten(_.values(this.standardSetMapFromProps)),
          "node"
        ),
      };
    }

    const isStandardsV1 =
      fieldUID === STANDARD_UIDS[curriculumType] &&
      getShowNewStandardsUIInV1() &&
      !learningCourseId &&
      selectFromAllPYP;

    if (isStandardsV1) {
      return _.find(selectedElementsPYP, {
        fieldUID: STANDARD_UIDS[curriculumType],
      })?.resolvedMinimalTree;
    }

    return { [nodeKey]: _.get(field, "nodes") };
  };

  updatedSelectedElements = () => {
    const { curriculumType, learningCourseId, selectFromAllPYP } = this.props;
    const { selectedElementsPYP, initialFields } = this.state;
    const updatedSelectedElements = [];
    const isSubjectStandardsFilterEnabled = getIsSubjectStandardsFilterEnabled();
    _.forEach(selectedElementsPYP, item => {
      const fieldUID = _.get(item, "fieldUID");
      const field = _.find(
        isSubjectStandardsFilterEnabled &&
          !_.isEmpty(this.initialFieldsRef.current)
          ? this.initialFieldsRef.current
          : initialFields,
        field => _.get(field, "uid") == fieldUID
      );

      const nodeKey = curriculumType == "IB_PYP" ? fieldUID : "nodes";
      const resolvedMinimalTree = this.getResolvedMinimalTree({
        fieldUID,
        curriculumType,
        learningCourseId,
        selectFromAllPYP,
        selectedElementsPYP,
        field,
        nodeKey,
      });

      updatedSelectedElements.push({
        ...(_.omit(item, ["__typename", "id"]) || {}),
        resolvedMinimalTree,
      });
    });
    return updatedSelectedElements;
  };

  togglePYPElement = (fieldUID, value) => {
    const { parentType, taggableElements, selectFromAllPYP } = this.props;
    const { selectedElementsPYP } = this.state;
    const taggablePYPElement = _.find(taggableElements, {
      value: fieldUID,
    });

    const currentParentType = this.getCurrentParentType();
    let selectedElementsPYPClone = _.cloneDeep(selectedElementsPYP);

    const selectedElement = _.find(selectedElementsPYPClone, { fieldUID });
    if (
      fieldUID === "action" &&
      selectedElement?.value?.length > 0 &&
      !ACLStore.can("FeatureFlag:EnableAction")
    ) {
      selectedElementsPYPClone = _.filter(
        selectedElementsPYPClone,
        item => item.fieldUID !== "action"
      );
    } else if (selectedElement) {
      const index = _.findIndex(selectedElementsPYPClone, { fieldUID });
      let ids = selectedElementsPYPClone[index].value;
      if (taggablePYPElement.isNestedView) {
        ids = value;
      }
      // If ID exists, remove it else add it (Toggling)
      else if (ids.includes(value)) ids = ids.filter(id => id !== value);
      else ids.push(value);
      selectedElementsPYPClone[index].value = ids;
    } else {
      value = taggablePYPElement.isNestedView ? value : [value];
      selectedElementsPYPClone.push({
        fieldUID,
        value,
        parentType: currentParentType,
      });
    }

    this.setState(
      {
        selectedElementsPYP: selectedElementsPYPClone,
      },
      this.updateSelectedElementsCount
    );
  };
  setSelectedStandards = selectedStandards => {
    this.setState({ selectedStandards }, this.updateSelectedElementsCount);
  };

  setSelectedStandardsInView = selectedStandards => {
    const { updateStateChange, isStateChanged } = this.props;
    const { selectedElementsPYP } = this.state;
    const selectedElement = _.find(selectedElementsPYP, {
      fieldUID: this.state.selectedPypElement,
    });
    if (!selectedElement) {
      const newPypElement = {
        fieldUID: this.state.selectedPypElement,
        value: [...selectedStandards],
        parentType: this.getCurrentParentType(),
      };
      const newSelectedElementsPYP = [
        ...this.state.selectedElementsPYP,
        newPypElement,
      ];
      this.setState(
        {
          selectedElementsPYP: newSelectedElementsPYP,
          selectedStandards: _.uniq([...selectedStandards]),
        },
        this.updateSelectedElementsCount
      );
    } else {
      selectedElement.value = _.uniq([...selectedStandards]);
      this.setState(
        {
          selectedElementsPYP: [...selectedElementsPYP],
          selectedStandards: _.uniq([...selectedStandards]),
        },
        this.updateSelectedElementsCount
      );
      if (!isStateChanged) {
        updateStateChange(true);
      }
    }
  };

  setSelectedStandardsInViewSearch = ({
    selectedStandardValues,
    selectedElementsPYPValue,
  }) => {
    const { updateStateChange, isStateChanged } = this.props;
    const { selectedStandards, selectedElementsPYP } = this.state;
    const selectedElement = _.find(selectedElementsPYP, {
      fieldUID: this.state.selectedPypElement,
    });
    const updatedSelectedStandards = _.difference(
      selectedStandardValues,
      selectedStandards
    );
    if (!selectedElement) {
      const newPypElement = {
        fieldUID: this.state.selectedPypElement,
        value: _.uniq([...updatedSelectedStandards, ...selectedStandardValues]),
        parentType: this.getCurrentParentType(),
      };
      const newSelectedElementsPYP = [
        ...this.state.selectedElementsPYP,
        newPypElement,
      ];
      this.setState(
        {
          selectedElementsPYP: newSelectedElementsPYP,
          selectedStandards: _.uniq([
            ...updatedSelectedStandards,
            ...selectedStandardValues,
          ]),
        },
        this.updateSelectedElementsCount
      );
    } else {
      selectedElement.value = _.uniq([
        ...updatedSelectedStandards,
        ...selectedStandardValues,
      ]);
      this.setState(
        {
          selectedElementsPYP: [...selectedElementsPYP],
          selectedStandards: _.uniq([
            ...updatedSelectedStandards,
            ...selectedStandardValues,
          ]),
        },
        this.updateSelectedElementsCount
      );
      if (!isStateChanged) {
        updateStateChange(true);
      }
    }
  };
  updateSelectedElementsCount = () => {
    const {
      updateSelectedElementsCount,
      selectFromAllPYP,
      learningCourseId,
    } = this.props;
    const { selectedElementsPYP } = this.state;
    const updatedSelectedElementsPYP =
      selectFromAllPYP && getIsStandardsRevampEnabled() && learningCourseId
        ? _.filter(
            selectedElementsPYP,
            item =>
              !getIsSubjectStandard({
                type: _.get(pypElementMapping, item.fieldUID),
              })
          )
        : selectedElementsPYP;
    let selectedCount = 0;
    _.forEach(updatedSelectedElementsPYP, item => {
      selectedCount += item.value.length;
    });

    updateSelectedElementsCount(selectedCount);
  };

  fetchStandardsSearchData = async ({ searchTerm }) => {
    const {
      getStandardSearchResults,
      learningCourseId,
      isPYPCurriculum,
      selectFromAllPYP,
      standardSetIdFromProps,
      fields,
      curriculumType,
    } = this.props;
    const standardSetIds = isPYPCurriculum
      ? _.uniq(
          !selectFromAllPYP && _.size(standardSetIdFromProps)
            ? standardSetIdFromProps
            : _.keys(this.props.standardSetMap)
        )
      : _.keys(this.props.standardSetMap);
    if (
      getIsNewGradingSetupEnabled() &&
      _.size(standardSetIds) &&
      searchTerm &&
      learningCourseId
    ) {
      const searchResults = await getStandardSearchResults({
        variables: {
          isPYPCurriculum: isPYPCurriculum || false,
          id: this.props.learningCourseId,
          filters: {
            searchText: searchTerm,
            archivalState: "ACTIVE",
            standardSetIds,
          },
        },
      });
      this.setState({
        searchStandardValues: getStandardEdges({
          node: searchResults,
          showCourseStandards: true,
          ...(isPYPCurriculum
            ? {
                selectFromAllPYP,
                leValues:
                  getLeValues({
                    fields,
                    fieldUid: STANDARD_UIDS[curriculumType],
                  }) || [],
              }
            : {}),
        }),
      });
    }
  };

  updateFields = ({
    initialFields: initialFieldsValues,
    searchTerm,
    selectedFilterValue,
    selectedFilterValues = [],
  }) => {
    const { initialFields } = this.state;
    const { learningCourseId } = this.props;
    const jsonSelectedFilterValues = _.map(selectedFilterValues, value =>
      JSON.parse(value)
    );

    if (getIsNewGradingSetupEnabled() && !_.isEmpty(learningCourseId)) {
      this.fetchStandardsSearchData({
        searchTerm,
      });
    }

    const filteredFields = _.map(
      !_.isEmpty(initialFieldsValues) ? initialFieldsValues : initialFields,
      field => {
        const rows = getPYPElementsRowsFromField({
          uid: _.get(field, "uid"),
          searchTerm,
          selectedFilterValues: jsonSelectedFilterValues,
          isMultiSelectionFilterApplied: true,
          initialFields: !_.isEmpty(initialFieldsValues)
            ? initialFieldsValues
            : initialFields,
        });
        return {
          uid: _.get(field, "uid"),
          rows,
          isNestedView: _.get(field, "isNestedView", false),
        };
      }
    );
    let searchCount = 0;
    _.forEach(filteredFields, field => {
      if (field.isNestedView) {
        const rows = _.filter(
          _.get(field, "rows"),
          row => _.get(row, "depth", false) === 3
        );
        searchCount += _.size(rows);
      } else {
        searchCount += _.size(_.get(field, "rows", []));
      }
    });
    if (_.size(searchTerm) > 0) {
      this.setState({
        searchFilterData: filteredFields,
        searchTerm,
        selectedFilterValue,
        selectedFilterValues,
        showSearchFilter: false,
      });
    } else {
      this.setState({
        filteredFields,
        searchFilterData: filteredFields,
        searchTerm,
        selectedFilterValue,
        selectedFilterValues,
        showSearchFilter: false,
      });
    }
  };

  setNestedState = (uid, subjectId, selections) => {
    if (
      getIsNewGradingSetupEnabled() &&
      getIsSubjectStandard({ type: _.get(pypElementMapping, uid) }) &&
      this.props.learningCourseId
    ) {
      this.setSelectedStandardsInViewSearch({
        selectedStandardValues: _.uniq(selections),
        selectedElementsPYPValue: uid,
      });
    } else {
      let { nestedState } = this.getNestedElementData({ uid });
      nestedState = {
        ...nestedState,
        [subjectId]: selections,
      };
      const benchmarkStateArray = _.map(nestedState, item => item);
      this.togglePYPElement(uid, _.flatten(benchmarkStateArray));
    }
  };

  processTaggableNodes = ({ field, taggableElement }) => {
    const key =
      taggableElement?.value === "lp"
        ? "learnerProfiles"
        : taggableElement?.value;

    const nodes =
      _.get(field, [
        "resolvedMinimalTree",
        key === "action" && ACLStore.can("FeatureFlag:EnableAction")
          ? "actions"
          : key,
      ]) ||
      _.get(field, ["resolvedMinimalTree", "nodes"]) ||
      _.get(field, "nodes") ||
      [];
    let updatedNodes = _.cloneDeep(nodes);

    if (!_.get(taggableElement, "isSubjectSpecific")) {
      return { nodes, updatedNodes };
    }

    let associatedParentNodes = getUniqAssociatedParentsOfNodes({ nodes });
    associatedParentNodes = _.filter(
      associatedParentNodes,
      item => _.get(item, "__typename") == "Subject"
    );

    updatedNodes = mergeAssociatedParentsWithNodes({
      associatedParentNodes,
      nodes: updatedNodes,
    });

    return { nodes, updatedNodes };
  };

  processTaggableNodesV2 = ({ fields, taggableElement }) => {
    const KEY_LEARNER_PROFILES = "learnerProfiles";
    const nodeKey =
      taggableElement?.value === "lp"
        ? KEY_LEARNER_PROFILES
        : taggableElement?.value;

    // Extract and flatten nodes from all fields
    const fieldNodes = _.flatMap(fields, field => {
      const resolvedMinimalTree = _.get(field, "resolvedMinimalTree", {});
      const actualKey =
        nodeKey === "action" && ACLStore.can("FeatureFlag:EnableAction")
          ? "actions"
          : nodeKey;

      return (
        _.get(resolvedMinimalTree, actualKey) ||
        _.get(resolvedMinimalTree, "nodes") ||
        _.get(field, "nodes") ||
        []
      );
    });

    // Filter archived nodes and remove duplicates
    const nodes = _.uniqBy(
      _.filter(fieldNodes, node => !_.get(node, "resolvedSubject.isArchived")),
      "id"
    );

    let updatedNodes = _.cloneDeep(nodes);

    if (!_.get(taggableElement, "isSubjectSpecific")) {
      return { nodes, updatedNodes };
    }

    let associatedParentNodes = getUniqAssociatedParentsOfNodes({ nodes });
    associatedParentNodes = _.filter(
      associatedParentNodes,
      item => _.get(item, "__typename") === "Subject"
    );

    updatedNodes = mergeAssociatedParentsWithNodes({
      associatedParentNodes,
      nodes: updatedNodes,
    });

    return { nodes, updatedNodes };
  };

  getInitialFields = ({
    fields,
    benchmarkData,
    taggableElements,
    plannerElements,
    subjectPlannerElements,
    subjectGradePlannerElements,
    gradePlannerElements,
    curriculumType,
    selectFromAllPYP,
    interdisciplinaryCriteriaMYPElements,
    elementPYP,
    isPostCreationModeNew,
    isTaggedFromRecentPost,
    parentType,
    standardSetMap,
  }) => {
    const updatedSubjectGradePlannerElements = [
      {
        label: "Subject Standards",
        nodes: _.map(_.flatten(_.values(standardSetMap)), "node"),
        type: "UBD_LEARNING_STANDARD",

        __typename: "PlannerElementSet",
      },
    ];
    const updateBenchmarkData =
      isPostCreationModeNew && !isTaggedFromRecentPost
        ? _.filter(
            benchmarkData,
            benchmark => !_.get(benchmark, "resolvedSubject.isArchived")
          )
        : benchmarkData;
    const newFields = _.filter(
      [
        ...(fields || []),
        selectFromAllPYP
          ? {
              uid: "benchmarks",
              resolvedMinimalTree: {
                benchmarks: updateBenchmarkData,
              },
            }
          : null,
      ],
      item => !!item
    );
    const filteredInitialFields = [];
    const combinePlannerElements = [
      ...(subjectPlannerElements || []),
      ...(subjectGradePlannerElements
        ? subjectGradePlannerElements
        : updatedSubjectGradePlannerElements || []),
      ...(gradePlannerElements || []),
      ...(plannerElements || []),
      ...(interdisciplinaryCriteriaMYPElements || []),
    ];
    _.forEach(taggableElements, taggableElement => {
      if (selectFromAllPYP && curriculumType != "IB_PYP") {
        const field =
          taggableElement?.value === "interdisciplinaryCriteriaMYP"
            ? _.find(combinePlannerElements, element => {
                return element.subtypes === "INTERDISCIPLINARY_CRITERIA";
              })
            : _.find(combinePlannerElements, {
                type: taggableElement.elementType,
              });
        const { nodes, updatedNodes } = this.processTaggableNodes({
          field,
          taggableElement,
        });
        filteredInitialFields.push({
          id: field?.id,
          uid: taggableElement.value,
          resolvedMinimalTree: {
            nodes: updatedNodes,
          },
          nodes,
          isNestedView: taggableElement.isNestedView,
        });
      } else {
        const field = _.find(newFields, { uid: taggableElement.value });
        const updatedFields = _.filter(newFields, {
          uid: taggableElement.value,
        });
        const { nodes, updatedNodes } =
          _.isEqual(parentType, "UNIT_PLAN") && getIsNewGoalTaggingEnabled()
            ? this.processTaggableNodesV2({
                fields: updatedFields,
                taggableElement,
              })
            : this.processTaggableNodes({
                field,
                taggableElement,
              });
        if (field)
          filteredInitialFields.push({
            ...field,
            resolvedMinimalTree: {
              nodes: updatedNodes,
            },
            nodes,
            isNestedView: taggableElement.isNestedView,
          });
      }
    });
    return filteredInitialFields;
  };

  getSearchResultCount = uid => {
    const { searchFilterData, searchTerm, searchStandardValues } = this.state;
    if (
      getIsNewGradingSetupEnabled() &&
      getIsSubjectStandard({ type: _.get(pypElementMapping, uid) }) &&
      this.props.learningCourseId
    ) {
      const mapValues = _.map(
        _.flatten(_.values(this.props.standardSetMap)),
        "node"
      );
      const count = _.size(
        _.map(_.flatten(_.values(searchStandardValues)), "node")
      );

      return count;
    }
    const { selectFromAllPYP } = this.props;
    const filteredField = _.find(searchFilterData, {
      uid: uid,
    });
    let elementsIdMap = {};
    if (uid === "benchmarks" && !selectFromAllPYP) {
      elementsIdMap = getBenchMarkNodesMemoized(filteredField);
    }
    let selectedCount = 0;
    if (searchTerm && filteredField) {
      if (filteredField.isNestedView) {
        const rows = _.filter(_.get(filteredField, "rows"), row => {
          let isLeaf =
            _.get(row, "depth", false) === 3 ||
            !_.size(_.get(row, "children", []));

          if (!selectFromAllPYP) {
            isLeaf = !_.some(_.get(row, "children"), childId =>
              _.has(elementsIdMap, childId)
            );
          }
          return isLeaf;
        });
        selectedCount = _.size(rows);
      } else {
        selectedCount = _.size(_.get(filteredField, "rows", []));
      }
    }
    return selectedCount;
  };

  getSelectedCount = uid => {
    const { selectedElementsPYP, filteredFields, searchTerm } = this.state;
    let selectedCount = 0;
    const selectedElementPYP = _.find(selectedElementsPYP, {
      fieldUID: uid,
    });
    if (selectedElementPYP) {
      selectedCount = selectedElementPYP.value?.length;
    }
    return selectedCount;
  };

  onSearchChange = searchTerm => {
    const { updateSearchText } = this.props;
    this.setState({ searchTerm });
    updateSearchText(searchTerm);
  };

  getNestedElementData = ({ uid, initalFieldsRef }) => {
    const { selectedElementsPYP, initialFields, filteredFields } = this.state;
    const { taggableElements } = this.props;
    const taggablePYPElement = _.find(taggableElements, {
      value: uid,
    });
    if (!taggablePYPElement) return {};
    const nestedNodes = getPYPElementsRowsFromUID({
      uid: uid,
      applySearchFilter: false,
      applyDropdownFilter: false,
      initialFields:
        !_.isEmpty(initalFieldsRef) && getIsSubjectStandardsFilterEnabled()
          ? initalFieldsRef
          : initialFields,
      filteredFields,
    });
    if (_.isEmpty(nestedNodes)) return {};

    const rootNodes = _.filter(nestedNodes, {
      depth: taggablePYPElement.startDepth,
    });
    const nestedState = {};

    _.forEach(rootNodes, rootNode => {
      let nodes = [];
      nodes = buildSlimTree({
        nodes: nestedNodes,
        selIds: [rootNode.id],
      });
      const selectedValues = _.intersection(
        _.get(_.find(selectedElementsPYP, { fieldUID: uid }), "value", []),
        _.map(nodes, item => item.id)
      );
      nestedState[rootNode.id] = selectedValues;
    });

    return {
      nestedState,
      rootNodes,
    };
  };

  getChildrenCount = (elements, filteredArray) => {
    const updatedIds = [];
    _.forEach(filteredArray, el => {
      _.forEach(_.get(el, "rows", []), row => {
        updatedIds.push(_.get(row, "id"));
      });
    });
    const count = _.intersection(elements, updatedIds).length;
    return count;
  };

  getSecondaryNavSelectedCount = uid => {
    const { filteredFields } = this.state;
    let selectedCount = 0;
    _.forEach(filteredFields, field => {
      const currentRow = _.get(field, "rows", []);
      if (_.size(currentRow) > 0) {
        _.forEach(currentRow, row => {
          if (row.parent == uid) {
            selectedCount += this.getChildrenCount(
              row.children,
              filteredFields
            );
          }
        });
      }
    });
    return selectedCount;
  };

  getAllSelectedPYPElements = () => {
    const { initialFields } = this.state;
    const allSelectedPYPElements = this.getAllFilteredSelectedElements({
      initialFields,
    });
    const allSelectedPYPElementsClone = [];
    _.forEach(allSelectedPYPElements, element => {
      const currentElement = _.get(element, "value", []);
      _.forEach(currentElement, item => {
        allSelectedPYPElementsClone.push(item);
      });
    });
    return allSelectedPYPElementsClone;
  };

  getCheckBoxCount = ({ uid, currentElement }) => {
    let leafCount = 0;
    const el = _.find(currentElement, { id: uid });
    function traverse(element) {
      if (element?.isLeaf) {
        leafCount++;
      } else {
        _.forEach(element?.children, item => {
          traverse(_.find(currentElement, { id: item }));
        });
      }
    }
    traverse(el);
    return leafCount;
  };

  getExpandedNestedElement = ({ uid }) => {
    const {
      searchTerm,
      initialFields,
      selectedElementsPYP,
      filteredFields,
    } = this.state;
    const {
      taggableElements,
      curriculumType,
      t,
      learningCourseId,
      isLearningCourseEnabled,
    } = this.props;
    const selectedElement = _.find(selectedElementsPYP, { fieldUID: uid });
    const isStandardsUIRevamped =
      getIsStandardsV2() && !learningCourseId && !isLearningCourseEnabled;
    const taggablePYPElement = _.find(taggableElements, {
      value: uid,
    });
    if (!taggablePYPElement) return null;

    const isSubjectStandard =
      getIsNewGradingSetupEnabled() &&
      getIsSubjectStandard({
        type: _.get(pypElementMapping, uid),
      }) &&
      this.props.learningCourseId;

    const isSecondaryNavSelected = ({ uid, parent }) => {
      const selectedElement = _.find(initialFields, { uid: parent });
      const currentRow = _.get(selectedElement, "nodes", BLANK_ARRAY);
      const getAllSelectedPYPElements = _.memoize(
        this.getAllSelectedPYPElements
      );
      const allFilteredPypElements = getAllSelectedPYPElements();
      const newSelectedPypElements = [];
      const elementCount = 0;
      if (_.size(currentRow) > 0) {
        for (let i = 0; i < _.size(currentRow); i++) {
          const row = currentRow[i];
          if (row.parent == uid) {
            _.forEach(row.children, child => {
              newSelectedPypElements.push(child);
            });
          }
        }
        const intersection = _.intersection(
          newSelectedPypElements,
          allFilteredPypElements
        );
        if (parent === "syllabusContentDP") {
          const elementCount = this.getCheckBoxCount({
            uid,
            currentElement: currentRow,
          });
          return _.get(nestedState?.[uid], "length", 0) > 0 &&
            elementCount === _.get(nestedState?.[uid], "length", 0)
            ? "CHECKED"
            : _.get(nestedState?.[uid], "length", 0) > 0
            ? "INDETERMINATE"
            : false;
        }
        if (
          _.get(nestedState?.[uid], "length", 0) > 0 &&
          _.get(nestedState?.[uid], "length", 0) >= _.size(intersection)
        ) {
          return "CHECKED";
        } else if (_.get(nestedState?.[uid], "length", 0) > 0) {
          return "INDETERMINATE";
        }
      }
      return false;
    };

    const benchmarks = isSubjectStandard
      ? []
      : getPYPElementsRowsFromUID({
          uid: uid,
          applySearchFilter: true,
          applyDropdownFilte: true,
          initialFields,
          filteredFields,
        });
    if (_.isEmpty(benchmarks) && !isSubjectStandard) return null;

    const roots =
      getIsNewGradingSetupEnabled() &&
      getIsSubjectStandard({ type: _.get(pypElementMapping, uid) }) &&
      this.props.learningCourseId
        ? this.props.standardSets
        : _.filter(benchmarks, {
            depth: taggablePYPElement.startDepth,
          });

    const { nestedState = {} } =
      this.getNestedElementData({
        uid,
        initalFieldsRef: this.initialFieldsRef.current,
      }) || BLANK_OBJECT;
    const onItemSelected = data => {
      this.setNestedState(
        uid,
        data.subject.id,
        _.get(data, "selections", BLANK_ARRAY)
      );
    };
    const debouncedOnItemSelected = _.debounce(
      params => onItemSelected(params),
      100
    );

    const nodesComponents = parent =>
      _.map(roots, (subject, index) => {
        const { id, label, levelId } = subject;
        let nodes = [];
        nodes = buildSlimTree({
          nodes: benchmarks,
          selIds: [subject.id],
        });
        const rootNodes = _.map(
          _.filter(
            nodes,
            item =>
              item.depth === taggablePYPElement.startDepth + 1 &&
              item.parent == parent
          ),
          node => node.id
        );

        const elementValue = _.compact(
          getElementValueMemoize({
            valueKeys: _.map(nodes, node => node.id),
            fieldUID: "benchmarks",
            benchmarkSet: { nodes, rootNodes },
          })
        );
        const updateNestedtate = (selectedValues, callback) => {
          nestedState[subject?.id] = selectedValues;
          callback();
        };
        const onCheckboxClicked = selectedValues => {
          updateNestedtate(selectedValues, () => {
            debouncedOnItemSelected({
              subject,
              selections: selectedValues,
              nodes,
              rootNodes,
            });
          });
        };
        const plannerElementValue = getPlannerElementValue({
          nodes,
          isStandardsField:
            this.state.selectedPypElement === STANDARD_UIDS[curriculumType],
        });
        const flattenedNestedNodes = getFlattenedNestedNodes({
          nodes,
          rootNodes: rootNodes,
          value: plannerElementValue,
          isPlannerElement: true,
          sideNavigationTab: id,
          isSubjectPropInNode: true,
        });

        return !_.isEmpty(elementValue) ? (
          (this.state.selectedPypElement !== STANDARD_UIDS[curriculumType] &&
            isStandardsUIRevamped) ||
          !isStandardsUIRevamped ? (
            <BenchmarkView
              key={id}
              value={elementValue}
              startDepth={1}
              mode={"view"}
              isTagging={true}
              updateInputField={onCheckboxClicked}
              selectedValues={nestedState?.[subject?.id] || BLANK_ARRAY}
              isReverseCheckbox={false}
              checkboxItemStyle={styles.checkboxItemStyle}
              nodeSpanStyle={styles.nodeSpanStyle}
              nodeLabelStyle={styles.nodeLabelStyle}
              checkboxContainerStyle={styles.checkboxContainerStyle}
              showViewEmpty={false}
              isDsComponent={true}
              labelStyle={styles.labelStyles}
              changeTextView={true}
              testIdPrefix={JOURNAL_POST_CREATION_GOAL}
              defaultTopMargin={
                levelId === "L1" ||
                _.isEqual(this.state.selectedPypElement, "specifiedConcepts")
                  ? 8
                  : 0
              }
              shouldCheckIsLeafProperty={false}
              allowParentTagging={isParentTaggingEnabledForType({
                plannerElementType: taggablePYPElement.elementType,
              })}
            />
          ) : (
            <StandardTable
              updatePost={updatePost}
              selectedStandards={_.get(selectedElement, "value", BLANK_ARRAY)}
              setSelectedStandards={this.setSelectedStandardsInView}
              edges={flattenedNestedNodes}
              selectedSubject={subject}
              learningCourseId={
                this.props.learningCourseId && isLearningCourseEnabled
              }
            />
          )
        ) : null;
      });

    const intialSelectedElement =
      _.get(this, "state.selectedPypElementSecondary") ||
      _.get(roots, "[0].id");
    const getCurrentSelectedElement = data => {
      this.setState({ selectedPypElementSecondary: data });
    };

    const getSelectedCount = (uid, parent) => {
      if (
        getIsNewGradingSetupEnabled() &&
        getIsSubjectStandard({ type: _.get(pypElementMapping, parent) }) &&
        this.props.learningCourseId
      ) {
        return _.size(
          _.intersection(
            _.map(_.get(this?.props?.standardSetMap, uid), "node.id"),
            this.state.selectedStandards
          )
        );
      }
      return _.get(_.get(nestedState, uid), "length", 0);
    };

    const expandedContentView =
      isSubjectStandard && learningCourseId
        ? []
        : nodesComponents(intialSelectedElement);

    const { props: screenPanelProps } = this.props;
    const { updatePost } = screenPanelProps;
    return (
      <>
        <SideNavigation
          searchTerm={searchTerm}
          filteredNodes={roots}
          isLocale={false}
          identifier={"id"}
          getCurrentSelectedElement={getCurrentSelectedElement}
          selectedNodeId={
            this.state.selectedPypElementSecondary
              ? this.state.selectedPypElementSecondary
              : _.get(roots, "[0].id")
          }
          getSelectedCount={getSelectedCount}
          selectAllFromNav={this.selectAllInSearchSecondaryNav}
          isNavItemSelected={
            getIsSubjectStandard({ type: _.get(pypElementMapping, uid) }) &&
            getIsNewGradingSetupEnabled() &&
            this.props.learningCourseId
              ? this.isStandardSecondaryNavSelected
              : isSecondaryNavSelected
          }
          parent={uid}
          isPrimaryNav={false}
          standardMapValue={
            isSubjectStandard
              ? _.mapValues(this.props.standardSetMap, arr => _.size(arr))
              : undefined
          }
        />
        <div
          style={getContainerStyle(
            taggablePYPElement?.startDepth >= 2,
            isSubjectStandard
          )}
        >
          {isSubjectStandard ? (
            <StandardTable
              subjects={this.props.subjects}
              grades={this.props.grades}
              tags={this.props.tags}
              updatePost={updatePost}
              setStandardFilters={this.props.setStandardFilters}
              standardFilters={this.props.standardFilters}
              selectedStandardSetId={
                this.state.selectedPypElementSecondary
                  ? this.state.selectedPypElementSecondary
                  : _.get(roots, "[0].id")
              }
              selectedStandards={this.state.selectedStandards}
              setSelectedStandards={this.setSelectedStandardsInView}
              edges={_.get(
                this?.props?.standardSetMap,
                this?.state?.selectedPypElementSecondary ||
                  _.get(roots, "[0].id"),
                BLANK_ARRAY
              )}
              learningCourseId={this.props.learningCourseId}
            />
          ) : null}

          {_.compact(expandedContentView).length > 0 && !isSubjectStandard
            ? expandedContentView
            : null}
        </div>
      </>
    );
  };

  isPrimaryNavItemSelected = ({ uid }) => {
    if (
      getIsSubjectStandard({
        type: _.get(pypElementMapping, uid),
      }) &&
      getIsNewGradingSetupEnabled() &&
      this.props.learningCourseId
    ) {
      return this.isStandardPrimaryNavSelected();
    }

    const { selectedElementsPYP, initialFields } = this.state;
    const selectedElement = _.find(selectedElementsPYP, { fieldUID: uid });
    const allSelectedPYPElements = this.getAllFilteredSelectedElements({
      initialFields,
    });
    const pypElement = _.find(allSelectedPYPElements, { fieldUID: uid });
    if (
      _.get(selectedElement, "value", []).length ===
        _.get(pypElement, "value", []).length &&
      !_.isUndefined(selectedElement)
    ) {
      return "CHECKED";
    } else if (_.get(selectedElement, "value", []).length > 0) {
      return "INDETERMINATE";
    } else return false;
  };

  selectAllPrimaryNav = (uid, isChecked) => {
    const { selectedElementsPYP = [], allSelectedPYPElements } = this.state;
    if (
      getIsSubjectStandard({
        type: _.get(pypElementMapping, uid),
      }) &&
      getIsNewGradingSetupEnabled() &&
      this.props.learningCourseId
    ) {
      this.selectAllStandarsFromPrimartNav(uid);
      return;
    }
    const pypElement = _.find(allSelectedPYPElements, { fieldUID: uid });
    const isCurrentPypElementPresentInSelectedPypElements = !_.isEmpty(
      _.find(selectedElementsPYP, { fieldUID: uid })
    );
    if (isCurrentPypElementPresentInSelectedPypElements) {
      const index = _.findIndex(selectedElementsPYP, { fieldUID: uid });
      const selectedElement = selectedElementsPYP[index];
      selectedElement.value = _.get(pypElement, "value", []);
      selectedElement.parentType = _.get(
        pypElement,
        "parentType",
        this.getCurrentParentType()
      );
      const newSelectedElementsPYP = [...selectedElementsPYP];
      if (isChecked) {
        newSelectedElementsPYP[index] = selectedElement;
      } else {
        _.pullAt(newSelectedElementsPYP, index);
      }

      this.setState(
        { selectedElementsPYP: newSelectedElementsPYP },
        this.updateSelectedElementsCount
      );
    } else {
      const newPypElement = {
        fieldUID: uid,
        value: _.get(pypElement, "value", []),
        parentType: _.get(
          pypElement,
          "parentType",
          this.getCurrentParentType()
        ),
      };
      const newSelectedElementsPYP = [...selectedElementsPYP, newPypElement];
      this.setState(
        { selectedElementsPYP: newSelectedElementsPYP },
        this.updateSelectedElementsCount
      );
    }
  };

  renderSearchRows = uid => {
    const {
      selectedElementsPYP,
      searchTerm,
      initialFields,
      searchFilterData,
      filteredFields,
    } = this.state;
    const rows = getPYPElementsRowsFromUID({
      uid,
      initialFields,
      filteredFields: searchFilterData,
    });
    return _.map(rows, row => {
      const isChecked = _.find(selectedElementsPYP, {
        fieldUID: uid,
      })?.value?.includes(row.id);

      return (
        <div
          key={row.id}
          className={classes.pypElementSearchRowContainer}
          onClick={() => this.togglePYPElement(uid, row.id)}
          data-test-id="journal-postcreation-goal-searchitem"
        >
          <div className={classes.pypSearchElementText}>
            {_.isEmpty(searchTerm) ? (
              row.label
            ) : (
              <HighlightSearchText text={row.label} highlight={searchTerm} />
            )}
          </div>
          <div className={classes.searchCheckBoxContainer}>
            {this.renderCheckbox(isChecked)}
          </div>
        </div>
      );
    });
  };

  renderSearchSections = () => {
    const { t, taggableElements } = this.props;
    const { searchTerm, initialFields, searchFilterData } = this.state;
    const taggablePYPElements = taggableElements;
    const sections = [];

    _.forEach(taggablePYPElements, TAGGABLE_PYP_ELEMENT => {
      if (TAGGABLE_PYP_ELEMENT.isNestedView) {
        sections.push(
          this.renderNestedElement({ uid: TAGGABLE_PYP_ELEMENT.value })
        );
        return;
      }

      const rows = getPYPElementsRowsFromUID({
        uid: TAGGABLE_PYP_ELEMENT.value,
        initialFields,
        filteredFields: searchFilterData,
      });
      if (_.isEmpty(rows)) return;

      const selectedCount = this.getSearchResultCount(
        TAGGABLE_PYP_ELEMENT.value
      );
      const expandedContent = this.renderSearchRows(TAGGABLE_PYP_ELEMENT.value);
      sections.push(
        <div
          key={TAGGABLE_PYP_ELEMENT.value}
          className={classes.goalGroupContainer}
        >
          {!_.isEmpty(searchTerm) ? (
            <Accordian
              title={
                TAGGABLE_PYP_ELEMENT.localisedLabel ||
                t(TAGGABLE_PYP_ELEMENT.label)
              }
              count={selectedCount}
              expandedContent={expandedContent}
              hideContainerBorder={true}
              showAccordian={true}
              customTitleStyle={styles.mainHeaderStyles}
              isExpanded={true}
              showCountBadge={false}
              showLeftArrow={true}
              customHeaderClass={classes.accordianHeaderClass}
              customArrowContainerClass={classes.accordianArrowContainerClass}
              customCountStyle={styles.customCountStyle}
              headerStyle={{ padding: "8px 12px !important" }}
              mainContentContainerStyle={styles.linearViewContainerStyle}
              leftArrowStyle={styles.leftArrowStyle}
            />
          ) : (
            <div className={classes.onSearchSectionContainer}>
              <div className={classes.onSearchSectionHeader}>
                {/* localised labels are the labels which will be coming from the database and hance we dont need to localise it
                    in the below localised label will be only available when isDynamicPlannerElement is true else there will
                     be no key of localised label
                */}
                {TAGGABLE_PYP_ELEMENT.localisedLabel ||
                  t(TAGGABLE_PYP_ELEMENT.label)}
              </div>
              {expandedContent}
            </div>
          )}
        </div>
      );
    });
    return sections;
  };

  renderNestedElement = ({ uid } = {}) => {
    const {
      selectedElementsPYP,
      searchTerm,
      initialFields,
      searchFilterData,
    } = this.state;

    const { t, taggableElements, learningCourseId } = this.props;
    const taggablePYPElement = _.find(taggableElements, {
      value: uid,
    });
    if (!taggablePYPElement) return null;

    const isSubjectStandard =
      getIsNewGradingSetupEnabled() &&
      getIsSubjectStandard({ type: _.get(pypElementMapping, uid) }) &&
      learningCourseId;

    const benchmarks = getPYPElementsRowsFromUID({
      uid: uid,
      applySearchFilter: true,
      applyDropdownFilte: true,
      initialFields,
      filteredFields: searchFilterData,
    });
    if (_.isEmpty(benchmarks) && !isSubjectStandard) return null;

    let selectedCount = 0;
    const selectedElementPYP = _.find(selectedElementsPYP, {
      fieldUID: uid,
    });
    if (selectedElementPYP) {
      selectedCount = selectedElementPYP?.value?.length;
    }

    const expandedContent = this.getSearchExpandedElements({ uid });

    // When searching don't show Accordian
    if (!_.isEmpty(searchTerm) && _.size(_.compact(expandedContent)) > 0)
      return (
        <Accordian
          key={uid}
          title={
            isSubjectStandard
              ? t("common:standard_plural")
              : taggablePYPElement.localisedLabel || t(taggablePYPElement.label)
          }
          count={this.getSearchResultCount(uid)}
          expandedContent={expandedContent}
          hideContainerBorder={true}
          showAccordian={true}
          customTitleStyle={styles.mainHeaderStyles}
          isExpanded={true}
          mainContentContainerStyle={styles.mainContentContainerStyle}
          showLeftArrow={true}
          customHeaderClass={classes.accordianHeaderClass}
          customArrowContainerClass={classes.accordianArrowContainerClass}
          customCountStyle={styles.customCountStyle}
          headerStyle={{ padding: "8px 12px !important" }}
          leftArrowStyle={styles.leftArrowStyle}
        />
      );
    else return null;
  };

  iterateOverNestedFields = (row, array, uid, id) => {
    const { initialFields } = this.state;

    const field = _.find(initialFields, { uid: uid });
    const currentRow = _.get(field, "nodes", []);
    for (let i = 0; i < _.size(currentRow); i++) {
      const currentElement = currentRow[i];
      if (_.includes(row.children, currentElement.id)) {
        if (currentElement.levelId === "L1" || !row?.isLeaf) {
          this.iterateOverNestedFields(
            currentElement,
            array,
            uid,
            currentElement.id
          );
        } else {
          _.size(currentElement?.children) > 0 &&
            array.push(...currentElement.children);
        }
      }
    }
    if (row?.isLeaf) {
      array.push(id);
    }
  };

  selectAllStandarsFromSecondaryNav = (uid, parent) => {
    const { selectedStandards, selectedElementsPYP } = this.state;
    const { isStateChanged, updateStateChange } = this.props;
    const isChecked = this.isStandardSecondaryNavSelected({ uid });
    const tempSelectedStandards = _.get(this.props.standardSetMap, uid);
    const standards = _.map(tempSelectedStandards, item => item.node.id);
    if (isChecked !== "CHECKED") {
      const newPypElement = {
        fieldUID: parent,
        value: _.uniq([...selectedStandards, ...standards]),
        parentType: this.getCurrentParentType(),
      };
      let newSelectedElementsPYP = [...selectedElementsPYP, newPypElement];
      this.setSelectedStandards(_.uniq([...selectedStandards, ...standards]));
      const selectedElement = _.find(selectedElementsPYP, {
        fieldUID: parent,
      });
      if (selectedElement) {
        selectedElement.value = _.uniq([
          ...selectedElement.value,
          ...standards,
        ]);
        selectedElement.parentType = this.getCurrentParentType();
        newSelectedElementsPYP = _.uniqBy(
          [...selectedElementsPYP, selectedElement],
          "fieldUID"
        );
      }

      this.setState(
        { selectedElementsPYP: newSelectedElementsPYP },
        this.updateSelectedElementsCount
      );

      if (!isStateChanged) {
        updateStateChange(true);
      }
    } else {
      const newSelectedStandards = _.difference(selectedStandards, standards);
      const selectedElement = _.find(selectedElementsPYP, {
        fieldUID: parent,
      });
      if (selectedElement) {
        selectedElement.value = newSelectedStandards;
        selectedElement.parentType = this.getCurrentParentType();
        selectedElement.fieldUID = parent;
      }
      if (selectedElement?.value?.length > 0) {
        this.setState(
          {
            selectedElementsPYP: _.uniqBy(
              [...selectedElementsPYP, selectedElement],
              "fieldUID"
            ),
          },
          this.updateSelectedElementsCount
        );
        if (!isStateChanged) {
          updateStateChange(true);
        }
      } else {
        const newSelectedElementsPYP = _.filter(
          selectedElementsPYP,
          item => item.fieldUID !== parent
        );
        this.setState(
          { selectedElementsPYP: newSelectedElementsPYP },
          this.updateSelectedElementsCount
        );
        if (!isStateChanged) {
          updateStateChange(true);
        }
      }

      this.setSelectedStandards(newSelectedStandards);
    }
  };

  isStandardSecondaryNavSelected = ({ uid }) => {
    const { selectedStandards } = this.state;
    const tempSelectedStandards = _.get(this.props.standardSetMap, uid);
    const standards = _.map(tempSelectedStandards, item => item.node.id);

    if (
      _.intersection(selectedStandards, standards).length ===
        _.size(standards) &&
      !_.isEmpty(selectedStandards) &&
      _.size(standards) > 0
    ) {
      return "CHECKED";
    } else if (_.intersection(standards, selectedStandards).length > 0) {
      return "INDETERMINATE";
    } else {
      return false;
    }
  };

  isStandardPrimaryNavSelected = () => {
    const { selectedStandards } = this.state;
    if (this.standardCountRef.current === _.size(selectedStandards)) {
      return "CHECKED";
    } else if (_.size(selectedStandards) > 0) {
      return "INDETERMINATE";
    } else {
      return false;
    }
  };

  selectAllStandarsFromPrimartNav = uid => {
    const { selectedStandards } = this.state;
    const keys = _.keys(this.props.standardSetMap);
    const allStandards = _.flatMap(keys, key =>
      _.map(this.props.standardSetMap[key], item => item.node.id)
    );

    const isChecked = this.isStandardPrimaryNavSelected();
    if (isChecked !== "CHECKED") {
      this.setSelectedStandards(
        _.uniq([...selectedStandards, ...allStandards])
      );
      const { isStateChanged, updateStateChange } = this.props;
      const { selectedElementsPYP } = this.state;
      const selectedElement = _.find(selectedElementsPYP, {
        fieldUID: uid,
      });
      if (!selectedElement) {
        const newPypElement = {
          fieldUID: uid,
          value: _.uniq([...selectedStandards, ...allStandards]),
          parentType: this.getCurrentParentType(),
        };
        const newSelectedElementsPYP = _.uniqBy(
          [...selectedElementsPYP, newPypElement],
          "fieldUID"
        );
        this.setState(
          { selectedElementsPYP: newSelectedElementsPYP },
          this.updateSelectedElementsCount
        );
        if (!isStateChanged) {
          updateStateChange(true);
        }
      } else {
        selectedElement.value = _.uniq([
          ...selectedElement.value,
          ...allStandards,
        ]);
        selectedElement.parentType = this.getCurrentParentType();
        const newSelectedElementsPYP = _.uniqBy(
          [...selectedElementsPYP, selectedElement],
          "fieldUID"
        );
        this.setState(
          { selectedElementsPYP: newSelectedElementsPYP },
          this.updateSelectedElementsCount
        );
        if (!isStateChanged) {
          updateStateChange(true);
        }
      }
    } else {
      const newSelectedStandards = _.difference(
        selectedStandards,
        allStandards
      );
      const { selectedElementsPYP } = this.state;
      const selectedElementIndex = _.findIndex(selectedElementsPYP, {
        fieldUID: uid,
      });
      if (selectedElementIndex !== -1) {
        _.pullAt(selectedElementsPYP, selectedElementIndex);
      }
      this.setState({ selectedElementsPYP }, this.updateSelectedElementsCount);
      if (!this.props.isStateChanged) {
        this.props.updateStateChange(true);
      }
      this.setSelectedStandards(newSelectedStandards);
    }
  };

  selectAllInSearchSecondaryNav = (uid, isChecked, parent) => {
    const {
      initialFields,
      selectedElementsPYP,
      allSelectedPYPElements,
    } = this.state;

    if (
      getIsSubjectStandard({
        type: _.get(pypElementMapping, parent),
      }) &&
      getIsNewGradingSetupEnabled() &&
      this.props.learningCourseId
    ) {
      this.selectAllStandarsFromSecondaryNav(uid, parent);
      return;
    }
    const { isStateChanged, updateStateChange } = this.props;
    const newSelectedPypElements = [];
    const allFilteredPypElements = this.getAllSelectedPYPElements();
    const getCurrentPypElement = element =>
      _.find(allSelectedPYPElements, { fieldUID: element });
    let parentPypElementId = "";
    let parentPypElementType = "";
    let isPypParentElementFound = false;
    for (let i = 0; i < _.size(initialFields); i++) {
      const field = _.get(initialFields, i, {});
      const currentPypElement = getCurrentPypElement(_.get(field, "uid", ""));
      const isCurrentPypElementPresentInSelectedPypElements = !_.isEmpty(
        _.find(selectedElementsPYP, { fieldUID: _.get(field, "uid", "") })
      );

      //Preventing the loop to run for the current element in case of select all and unselect all if parent is different
      if (parent != field?.uid) {
        continue;
      }
      const currentRow = _.get(field, "nodes", []);
      const elementsIdMap = {};
      _.forEach(currentRow, node => {
        _.set(elementsIdMap, _.get(node, "id"), true);
      });
      if (_.size(currentRow) > 0) {
        _.forEach(currentRow, row => {
          if (
            row.parent == uid ||
            _.find(row?.associatedParents, { id: uid })
          ) {
            if (
              _.size(row.children) > 0 &&
              _.some(_.get(row, "children"), childId =>
                _.has(elementsIdMap, childId)
              )
            ) {
              if (row.levelId === "L1" || !row?.isLeaf) {
                _.forEach(row.children, child => {
                  newSelectedPypElements.push(child);
                });
                this.iterateOverNestedFields(
                  row,
                  newSelectedPypElements,
                  _.get(field, "uid", "")
                );
              } else {
                _.forEach(row.children, child => {
                  newSelectedPypElements.push(child);
                });
              }
            } else {
              newSelectedPypElements.push(_.get(row, "id"));
            }
            isPypParentElementFound = true;
            parentPypElementId = field?.uid;
            parentPypElementType = currentPypElement?.parentType;
          }
        });
      }
      if (isPypParentElementFound) {
        const intersection = _.intersection(
          newSelectedPypElements,
          allFilteredPypElements
        );

        if (isCurrentPypElementPresentInSelectedPypElements) {
          const selectedElement = _.find(selectedElementsPYP, {
            fieldUID: parentPypElementId,
          });
          if (selectedElement) {
            const index = _.findIndex(selectedElementsPYP, {
              fieldUID: parentPypElementId,
            });
            if (isChecked) {
              selectedElement.value = _.uniq([
                ..._.get(selectedElement, "value", []),
                ...intersection,
              ]);
              selectedElement.parentType =
                parentPypElementType || this.getCurrentParentType();
              _.set(selectedElementsPYP, index, selectedElement);
            } else {
              selectedElement.value = _.difference(
                selectedElement.value,
                intersection
              );
              selectedElement.parentType =
                parentPypElementType || this.getCurrentParentType();
              _.set(selectedElementsPYP, index, selectedElement);

              _.isEmpty(_.get(selectedElement, "value")) &&
                _.pullAt(selectedElementsPYP, index);
            }

            this.setState(
              { selectedElementsPYP },
              this.updateSelectedElementsCount
            );
            if (!isStateChanged) {
              updateStateChange(true);
            }
          }
        } else {
          const newPypElement = {
            fieldUID: parentPypElementId,
            value: isChecked ? intersection : [],
            parentType: parentPypElementType || this.getCurrentParentType(),
          };
          const newSelectedElementsPYP = [
            ...selectedElementsPYP,
            newPypElement,
          ];
          this.setState(
            { selectedElementsPYP: newSelectedElementsPYP },
            this.updateSelectedElementsCount
          );
          if (!isStateChanged) {
            updateStateChange(true);
          }
        }
        break;
      } else {
        //If parent is not found in the current element then check if the parent is present in the selected elements if not then add it else update the value
        const isCurrentPypElementPresentInSelectedPypElements = !_.isEmpty(
          _.find(selectedElementsPYP, { fieldUID: parent })
        );
        if (isCurrentPypElementPresentInSelectedPypElements) {
          const selectedElement = _.find(selectedElementsPYP, {
            fieldUID: parent,
          });
          if (selectedElement) {
            const index = _.findIndex(selectedElementsPYP, {
              fieldUID: parentPypElementId,
            });
            if (isChecked && _.includes(allFilteredPypElements, uid)) {
              selectedElement.value = _.uniq([
                ..._.get(selectedElement, "value", []),
                uid,
              ]);
              selectedElement.parentType =
                parentPypElementType || this.getCurrentParentType();
              _.set(selectedElementsPYP, index, selectedElement);
            } else {
              _.pull(selectedElement?.value, uid);
              _.set(selectedElementsPYP, index, selectedElement);

              _.isEmpty(_.get(selectedElement, "value")) &&
                _.pullAt(selectedElementsPYP, index);
            }
            this.setState(
              { selectedElementsPYP },
              this.updateSelectedElementsCount
            );
            if (!isStateChanged) {
              updateStateChange(true);
            }
          }
        } else {
          if (isChecked && _.includes(allFilteredPypElements, uid)) {
            const newPypElement = {
              fieldUID: parent,
              value: [uid],
              parentType: "ALL",
            };
            const newSelectedElementsPYP = [
              ...selectedElementsPYP,
              newPypElement,
            ];
            this.setState(
              { selectedElementsPYP: newSelectedElementsPYP },
              this.updateSelectedElementsCount
            );
            if (!isStateChanged) {
              updateStateChange(true);
            }
          } else {
            const selectedElement = _.find(selectedElementsPYP, {
              fieldUID: parent,
            });
            const index = _.findIndex(selectedElementsPYP, {
              fieldUID: parent,
            });
            if (selectedElement) {
              _.pull(selectedElement?.value, uid);
              _.set(selectedElementsPYP, index, selectedElement);
            }

            _.isEmpty(_.get(selectedElement, "value")) &&
              _.pullAt(selectedElementsPYP, index);
            this.setState(
              { selectedElementsPYP },
              this.updateSelectedElementsCount
            );
            if (!isStateChanged) {
              updateStateChange(true);
            }
          }
        }
      }
    }
  };

  getSearchExpandedElements = ({ uid }) => {
    const {
      searchTerm,
      selectedFilterValue,
      initialFields,
      searchFilterData,
      selectedStandards,
      searchStandardValues,
    } = this.state;
    const { taggableElements } = this.props;
    const taggablePYPElement = _.find(taggableElements, {
      value: uid,
    });
    if (!taggablePYPElement) return null;

    const benchmarks = getPYPElementsRowsFromUID({
      uid: uid,
      applySearchFilter: true,
      applyDropdownFilte: true,
      initialFields,
      filteredFields: searchFilterData,
    });

    const isSubjectStandard =
      getIsNewGradingSetupEnabled() &&
      getIsSubjectStandard({ type: _.get(pypElementMapping, uid) }) &&
      this.props.learningCourseId;
    if (_.isEmpty(benchmarks) && !isSubjectStandard) return null;

    const roots = isSubjectStandard
      ? _.filter(this.props.standardSets, set =>
          _.includes(_.keys(searchStandardValues), set.id)
        )
      : _.filter(benchmarks, {
          depth: taggablePYPElement.startDepth,
        });

    const { nestedState } = this.getNestedElementData({ uid });
    let nodesComponents = [];

    const isSearchNavOptionSelected = ({ uid, parent }) => {
      if (isSubjectStandard) {
        return this.isStandardSecondaryNavSelected({ uid });
      }
      const selectedElement = _.find(initialFields, { uid: parent });
      const currentRow = _.get(selectedElement, "nodes", []);
      const getAllSelectedPYPElements = _.memoize(
        this.getAllSelectedPYPElements
      );
      const allFilteredPypElements = getAllSelectedPYPElements();
      const newSelectedPypElements = [];
      if (_.size(currentRow) > 0) {
        for (let i = 0; i < _.size(currentRow); i++) {
          const row = currentRow[i];
          if (row.parent == uid) {
            _.forEach(row.children, child => {
              newSelectedPypElements.push(child);
            });
          }
        }
        const intersection = _.intersection(
          newSelectedPypElements,
          allFilteredPypElements
        );
        if (
          _.get(nestedState?.[uid], "length", 0) > 0 &&
          _.get(nestedState?.[uid], "length", 0) >= _.size(intersection)
        ) {
          return "CHECKED";
        } else if (_.get(nestedState?.[uid], "length", 0) > 0) {
          return "INDETERMINATE";
        }
      }
      return false;
    };

    nodesComponents = _.map(roots, (subject, index) => {
      const { id, label } = subject;
      let nodes = [];
      nodes = isSubjectStandard
        ? _.map(_.flatten(_.values(searchStandardValues)), "node")
        : buildSlimTree({
            nodes: benchmarks,
            selIds: [subject.id],
          });
      const nodeIdSet =
        isSubjectStandard && this.props.isPYPCurriculum
          ? new Set(_.map(nodes, "id"))
          : null;
      let rootNodes;
      if (isSubjectStandard) {
        rootNodes = this.props.isPYPCurriculum
          ? _.map(
              _.filter(nodes, node => {
                const parentId =
                  _.get(node, "parentData.id") || _.get(node, "parent");
                return !parentId || !nodeIdSet.has(parentId);
              }),
              node => node.id
            )
          : _.map(nodes, node => node.id);
      } else {
        rootNodes = _.map(
          _.filter(
            nodes,
            item => item.depth === taggablePYPElement.startDepth + 1
          ),
          node => node.id
        );
      }
      return _.size(rootNodes) > 0 ? (
        <SubjectBenchmarks
          key={id}
          selectedValues={
            isSubjectStandard
              ? selectedStandards
              : nestedState?.[subject?.id] || BLANK_ARRAY
          }
          subject={{
            value: subject,
            label: label,
            id: id,
            subjectId: isSubjectStandard ? id : subject.subject,
          }}
          nodes={nodes}
          rootNodes={rootNodes}
          onItemSelected={data => {
            this.setNestedState(
              uid,
              data.subject.id,
              _.get(data, "selections", [])
            );
          }}
          isReverseCheckbox={true}
          hideContainerBorder={true}
          showLabelBorder={index !== roots.length - 1}
          showAccordian={false}
          showCountSeperate={true}
          checkboxItemStyle={styles.checkboxItemStyle}
          customTitleStyle={styles.customTitleStyle}
          nodeSpanStyle={styles.nodeSpanStyle}
          isExpanded={!_.isEmpty(selectedFilterValue)}
          checkboxContainerStyle={{ height: "fit-content", paddingLeft: "8px" }}
          showCountBadge={true}
          curriculumType={_.get(this.props, "curriculumType", "")}
          searchTerm={searchTerm}
          parent={uid}
          isSearchNavOptionSelected={isSearchNavOptionSelected}
          selectAllSecondaryNav={this.selectAllInSearchSecondaryNav}
          shouldCheckIsLeafProperty={false}
          showSearchLabel={isSubjectStandard}
        />
      ) : null;
    });
    return nodesComponents;
  };

  renderSections = () => {
    const {
      t,
      taggableElements,
      props: screenPanelProps,
      showFilters,
      standardsTagFilter,
      curriculumType,
      learningCourseId,
      standardLabel,
    } = this.props;
    const selectedPypElement = _.get(
      screenPanelProps,
      "postDetails.selectedPypElement",
      []
    );
    const { searchTerm, initialFields, filteredFields } = this.state;
    const taggablePYPElements = taggableElements;
    const sections = [];
    const primaryPYPElements = [];
    const totalStandardsCount = _.sum(
      _.values(_.mapValues(this.props.standardSetMap, arr => _.size(arr)))
    );
    let benchmarkData = null;
    _.forEach(taggablePYPElements, TAGGABLE_PYP_ELEMENT => {
      const rows = getPYPElementsRowsFromUID({
        uid: TAGGABLE_PYP_ELEMENT.value,
        initialFields,
        filteredFields,
      });
      if (TAGGABLE_PYP_ELEMENT.value === STANDARD_UIDS[curriculumType]) {
        benchmarkData = {
          rows,
          uid: TAGGABLE_PYP_ELEMENT.value,
        };
      }
      const showStandards =
        (totalStandardsCount > 0 &&
          getIsNewGradingSetupEnabled() &&
          learningCourseId &&
          getIsSubjectStandard({ type: TAGGABLE_PYP_ELEMENT.elementType })) ||
        (!_.isEmpty(standardsTagFilter) && showFilters);

      if (!_.isEmpty(rows) || showStandards) {
        if (
          getIsNewGradingSetupEnabled() && learningCourseId
            ? !getIsSubjectStandard({ type: TAGGABLE_PYP_ELEMENT.elementType })
            : true
        ) {
          primaryPYPElements.push(TAGGABLE_PYP_ELEMENT);
        } else if (
          getIsSubjectStandard({ type: TAGGABLE_PYP_ELEMENT.elementType }) &&
          getIsNewGradingSetupEnabled() &&
          learningCourseId
        ) {
          primaryPYPElements.push({
            label: standardLabel ?? t("common:standard_plural"),
            isDynamicPlannerElementLabel: true,
            value: TAGGABLE_PYP_ELEMENT.value,
            isNestedView: true,
            startDepth: 0,
            elementType: "UBD_LEARNING_STANDARD",
            isGradeSpecific: true,
            isSubjectSpecific: true,
            localisedLabel: "Standards",
          });
          if (
            _.size(primaryPYPElements) === 1 &&
            !this.state.selectedPypElement
          ) {
            this.setState({
              selectedPypElement: _.first(primaryPYPElements)?.value,
            });
          }
        }
      }
      if (_.isEmpty(rows) && !showStandards) {
        return;
      }

      sections.push(
        <div
          key={TAGGABLE_PYP_ELEMENT.value}
          className={classes.goalGroupContainer}
        ></div>
      );
    });
    const getInitialSelectedElement = filteredArray => {
      for (let i = 0; i < _.size(filteredArray); i++) {
        if (_.get(filteredArray[i], "rows.length", 0) > 0) {
          return _.get(filteredArray[i], "uid");
        }
      }
    };

    const intialSelectedElement = this.state.selectedPypElement
      ? this.state.selectedPypElement
      : this.state.searchTerm
      ? getInitialSelectedElement(filteredFields)
      : _.get(primaryPYPElements, "[0].value");

    const isCurrentSelectedElementNested = data =>
      _.size(primaryPYPElements) &&
      _.filter(
        primaryPYPElements,
        element => element.value === intialSelectedElement
      )?.[0]?.isNestedView;
    const isBenchmarkDataAvailable =
      getIsSubjectStandardsFilterEnabled() &&
      !_.isEmpty(standardsTagFilter) &&
      benchmarkData?.uid === STANDARD_UIDS[curriculumType] &&
      !_.isEmpty(benchmarkData?.rows);

    const getCurrentSelectedElement = data => {
      const {
        updateShowFilters,
        curriculumType,
        props: screenPanelProps,
      } = this.props;
      const { updatePost } = screenPanelProps;
      updateShowFilters(STANDARD_UIDS[curriculumType] === data);

      this.setState({
        selectedPypElement: data,
        selectedPypElementSecondary: "",
      });
      updatePost({
        selectedPypElement: data,
      });
    };

    return !_.isEmpty(_.compact(sections)) ? (
      <div className={classes.sectionContainer}>
        <SideNavigation
          searchTerm={searchTerm}
          filteredNodes={primaryPYPElements}
          getCurrentSelectedElement={getCurrentSelectedElement}
          identifier={"value"}
          selectedNodeId={
            this.state.selectedPypElement
              ? this.state.selectedPypElement
              : _.get(primaryPYPElements, "[0].value")
          }
          getSelectedCount={this.getSelectedCount}
          selectAllFromNav={this.selectAllPrimaryNav}
          isNavItemSelected={this.isPrimaryNavItemSelected}
          isPrimaryNav={true}
          standardLabel={
            standardLabel ??
            (learningCourseId
              ? t("common:standard_plural")
              : t("common:content_standards"))
          }
        />
        {(isCurrentSelectedElementNested(intialSelectedElement) &&
          _.isEmpty(standardsTagFilter)) ||
        isBenchmarkDataAvailable ? (
          this.getExpandedNestedElement({ uid: intialSelectedElement })
        ) : (
          <div style={styles.itemsViewContainerStyle}>
            {this.renderRows(intialSelectedElement)}
          </div>
        )}
      </div>
    ) : null;
  };

  renderRows = uid => {
    const {
      selectedElementsPYP,
      searchTerm,
      initialFields,
      filteredFields,
    } = this.state;
    const { standardsTagFilter } = this.props;
    const rows = getPYPElementsRowsFromUID({
      uid,
      initialFields,
      filteredFields,
    });

    const { t } = this.props;
    return _.size(rows) && _.isEmpty(standardsTagFilter) ? (
      _.map(rows, row => {
        const isChecked = _.find(selectedElementsPYP, {
          fieldUID: uid,
        })?.value?.includes(row.id);

        return (
          <div
            key={row.id}
            className={classes.pypElementRowContainer}
            onClick={() => this.togglePYPElement(uid, row.id)}
            data-test-id={`journal-postcreation-learning-goal-row-${row.id}`}
          >
            <div className={classes.checkBoxContainer}>
              {this.renderCheckbox(isChecked)}
            </div>
            <div className={classes.pypElementText}>
              {_.isEmpty(searchTerm) ? (
                row.label
              ) : (
                <HighlightSearchText text={row.label} highlight={searchTerm} />
              )}
            </div>
          </div>
        );
      })
    ) : (
      <EmptyState
        illustration={GoalIllustration}
        title={t("classRoom:nothing_here_yet")}
        subtitle={t("journal:learning_goals_empty_text")}
        size="small"
        subtitleStyle={emptyStateSubtitleStyleUnit}
      />
    );
  };

  setSearchFocusState = state => {
    this.setState({ searchFocused: state });
  };

  renderCheckbox = isChecked => {
    return (
      <Checkbox
        isChecked={isChecked}
        testId={"journal-postcreation-goal-searchitem"}
      />
    );
  };

  getFilteredTaggablePYPElements = () => {
    const { taggableElements } = this.props;
    const { initialFields, filteredFields } = this.state;
    const filteredTaggablePYPElements = [];
    _.forEach(taggableElements, TAGGABLE_PYP_ELEMENT => {
      const { value, isNestedView } = TAGGABLE_PYP_ELEMENT;
      // Don't show action in filter options
      if (
        ["action"].includes(value) &&
        !ACLStore.can("FeatureFlag:EnableAction")
      )
        return;

      if (isNestedView) {
        const { rootNodes } = this.getNestedElementData({
          uid: value,
        });
        if (!_.isEmpty(rootNodes))
          filteredTaggablePYPElements.push({
            ...TAGGABLE_PYP_ELEMENT,
            rootNodes,
          });
        return;
      }
      const rows = getPYPElementsRowsFromUID({
        uid: TAGGABLE_PYP_ELEMENT.value,
        applySearchFilter: false,
        applyDropdownFilter: false,
        initialFields,
        filteredFields,
      });
      if (!_.isEmpty(rows))
        filteredTaggablePYPElements.push(TAGGABLE_PYP_ELEMENT);
    });
    return filteredTaggablePYPElements;
  };

  getGoalsFilterLabel = totalGoalsCount => {
    const { t } = this.props;
    const { selectedFilterValues } = this.state;
    if (_.size(selectedFilterValues) == 0 || totalGoalsCount == 0) {
      return t("common:none");
    } else if (_.size(selectedFilterValues) == totalGoalsCount) {
      return t("common:all");
    } else return selectedFilterValues;
  };

  render() {
    const { t, containerRef } = this.props;
    const { searchTerm } = this.state;
    const sections = this.renderSections();
    const searchSections = this.renderSearchSections();
    const emptyLearningGoals = isEmpty(sections);

    const stopPropogation = e => {
      e.stopPropagation();
    };
    return (
      <div className={classes.container}>
        {_.size(searchTerm) > 0 ? (
          <Popover
            isOpen={true}
            align={"end"}
            positions={["bottom"]}
            reposition={true}
            containerStyle={styles.searchContainerStyle}
            boundaryInset={10}
            content={
              <div onClick={stopPropogation} ref={this.searchSectionRef}>
                {_.isEmpty(_.compact(searchSections)) ? (
                  <div className={classes.noDataContainer}>
                    <EmptyState
                      dsVersion="2.0"
                      illustration={NoSearchInFilterIllustration}
                      title={t("common:noResultHeading")}
                      subtitle={t("common:no_items_matching_your_search")}
                      size="small"
                      subtitleStyle={emptyStateSubtitleStyle}
                    />
                  </div>
                ) : (
                  searchSections
                )}
              </div>
            }
            childrenRef={{ current: containerRef.current }}
            isChildrenRefPassed={true}
            onClickOutside={() => this.onSearchChange("")}
          />
        ) : null}
        {(() => {
          let loadingComponent = null;
          if (this.props.isLoading && emptyLearningGoals && this.props.isData) {
            loadingComponent = !_.isUndefined(this.props.customLoadingComponent)
              ? this.props.customLoadingComponent
              : null;
          }
          return loadingComponent;
        })()}
        {emptyLearningGoals && !this.props.isLoading ? (
          searchTerm ? (
            <div className={classes.noDataContainer}>
              <EmptyState
                dsVersion="2.0"
                illustration={NoSearchInFilterIllustration}
                title={t("common:noResultHeading")}
                subtitle={t("common:no_items_matching_your_search")}
                size="small"
                subtitleStyle={emptyStateSubtitleStyle}
              />
            </div>
          ) : (
            <div className={classes.noDataContainer}>
              <EmptyState
                dsVersion="2.0"
                illustration={GoalIllustration}
                title={t("classRoom:nothing_here_yet")}
                subtitle={t("journal:learning_goals_empty_text")}
                size="small"
                subtitleStyle={emptyStateSubtitleStyleUnit}
              />
            </div>
          )
        ) : (
          sections
        )}
      </div>
    );
  }
}

export default TagLearningGoalsList;
